import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { blogPosts, heroImages, happenings, aboutImages } from "../data/mock";

const CDN = "https://customer-assets-m6fa6gv7.emergentagent.net/job_soul-food-table/artifacts";

const gallery = [
  { src: `${CDN}/96o3fiq4_IMG_2196.jpeg`, caption: "Evening service at the truck window", span: "md:col-span-7", aspect: "aspect-[16/10]" },
  { src: `${CDN}/36dzbbcq_IMG_2194.jpeg`, caption: "Community feast night — bunting, prayers, plates", span: "md:col-span-5", aspect: "aspect-[8/7]" },
  { src: "/media/community-1-poster.jpg", caption: "A plate, ready for the window", span: "md:col-span-4", aspect: "aspect-[3/4]" },
  { src: `${CDN}/idyinb16_IMG_2195.jpeg`, caption: "The queue gathering as the sun drops", span: "md:col-span-4", aspect: "aspect-[3/4]" },
  { src: "/media/community-2-poster.jpg", caption: "Piled generous, the Mamamina way", span: "md:col-span-4", aspect: "aspect-[3/4]" },
  { src: happenings[1]?.image, caption: "Skewers over the flame, Friday nights", span: "md:col-span-5", aspect: "aspect-[8/7]" },
  { src: heroImages[0], caption: "The table, set the Mamamina way", span: "md:col-span-7", aspect: "aspect-[16/10]" },
];

function InteriorGallery() {
  return (
    <section className="mt-24 pt-16 border-t border-[var(--mm-line)]" data-testid="stories-gallery">
      <div className="section-label mb-3">Step inside</div>
      <h2 className="font-display text-4xl md:text-6xl text-[var(--mm-cream)] leading-none mb-4">
        The place, the people.
      </h2>
      <p className="text-[var(--mm-cream-muted)] font-light leading-relaxed max-w-xl mb-12">
        A look around the truck and the tables — the queues, the smoke, the family behind the window.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-12 gap-4 md:gap-6">
        {gallery.map((g) => (
          <figure key={g.caption} className={`col-span-1 ${g.span}`}>
            <div className={`img-hover ${g.aspect} overflow-hidden`}>
              <img src={g.src} alt={g.caption} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <figcaption className="mt-3 text-xs tracking-[0.14em] text-[var(--mm-cream-muted)]">
              {g.caption}
            </figcaption>
          </figure>
        ))}
      </div>
      <div className="mt-12">
        <Link to="/#community" className="editorial-link">see more from the community</Link>
      </div>
    </section>
  );
}

export default function BlogList() {
  return (
    <div className="min-h-screen bg-[var(--mm-bg)] grain">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors mb-10">
          <ArrowLeft size={14} /> Back to Home
        </Link>

        <div className="section-label mb-3">Stories</div>
        <h1 className="font-display italic text-5xl md:text-7xl text-[var(--mm-cream)] leading-tight mb-14">
          From the <span className="text-[var(--mm-accent)]">kitchen.</span>
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {blogPosts.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="group">
              <div className="img-hover aspect-[16/10] overflow-hidden mb-5">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
              </div>
              <div className="flex items-center gap-4 text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-3">
                <span>{p.date}</span>
                <span className="flex items-center gap-1.5"><Clock size={11} /> {p.readMins} min read</span>
              </div>
              <h2 className="font-display italic text-3xl text-[var(--mm-cream)] leading-tight mb-3 group-hover:text-[var(--mm-accent)] transition-colors">
                {p.title}
              </h2>
              <p className="text-[var(--mm-cream-muted)] font-light leading-relaxed">{p.excerpt}</p>
            </Link>
          ))}
        </div>

        <InteriorGallery />
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-[var(--mm-bg)] grain">
      <div className="max-w-[820px] mx-auto px-6 md:px-12 py-14">
        <Link to="/blog" className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors mb-10">
          <ArrowLeft size={14} /> All Stories
        </Link>

        <div className="flex items-center gap-4 text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-4">
          <span>{post.date}</span>
          <span className="flex items-center gap-1.5"><Clock size={11} /> {post.readMins} min read</span>
        </div>
        <h1 className="font-display italic text-4xl md:text-6xl text-[var(--mm-cream)] leading-tight mb-8">
          {post.title}
        </h1>
        <div className="aspect-[16/9] overflow-hidden mb-10">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        </div>
        <div className="space-y-6 text-lg text-[var(--mm-cream)] font-light leading-relaxed">
          {post.body.map((para, i) => (<p key={i}>{para}</p>))}
        </div>
        <div className="mt-14 pt-10 border-t border-[var(--mm-line)] text-center">
          <Link to="/#menu" className="btn-primary">Order Now</Link>
        </div>
      </div>
    </div>
  );
}
